'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface QuizAnswers {
  type?: string;
  tier?: string;
  rate?: number;
  timezone?: string;
  times?: string[];
  insurance?: string;
  school?: string;
  ageVerification?: 'yes' | 'no';
  sessionType?: string;
  needs?: string[];
  therapistType?: string;
  availability?: string[];
  matchingQuizId?: string;
  promoCode?: string;
  partnershipId?: string;
  isPaidReferrer?: boolean;
  referralSource?: string;
  [key: string]: unknown;
}

export interface SelectedTherapist {
  id: string;
  firstName?: string;
  lastName?: string;
  slug?: string;
  profileImage?: string;
  displayName?: string;
  credential?: string;
  email?: string;
  therapistTier?: string;
  [key: string]: unknown;
}

interface AppContextType {
  // Quiz state (can be persisted across pages)
  quizAnswers: QuizAnswers | null;

  // Selected therapist for booking flow
  selectedTherapist: SelectedTherapist | null;

  // Methods
  setQuizAnswers: (answers: QuizAnswers) => void;
  clearQuizAnswers: () => void;
  setSelectedTherapist: (therapist: SelectedTherapist) => void;
  clearSelectedTherapist: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [quizAnswers, setQuizAnswersState] = useState<QuizAnswers | null>(null);
  const [selectedTherapist, setSelectedTherapistState] = useState<SelectedTherapist | null>(null);


  const setQuizAnswers = (answers: QuizAnswers) => {
    setQuizAnswersState(answers);
  };

  const clearQuizAnswers = () => {
    setQuizAnswersState(null);
  };

  const setSelectedTherapist = (therapist: SelectedTherapist) => {
    setSelectedTherapistState(therapist);
  };

  const clearSelectedTherapist = () => {
    setSelectedTherapistState(null);
  };
  
  
  
  const value: AppContextType = {
    quizAnswers,
    selectedTherapist,
    setQuizAnswers,
    clearQuizAnswers,
    setSelectedTherapist,
    clearSelectedTherapist,
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// Keep useApp private - use specific hooks instead
function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Convenience hooks for app state
export function useQuiz() {
  const { quizAnswers, setQuizAnswers, clearQuizAnswers } = useApp();
  return { quizAnswers, setQuizAnswers, clearQuizAnswers };
}

export function useSelectedTherapist() {
  const { selectedTherapist, setSelectedTherapist, clearSelectedTherapist } = useApp();
  return { selectedTherapist, setSelectedTherapist, clearSelectedTherapist };
}